import React, {
  useState,
  useCallback,
  useContext,
  useRef,
  useEffect,
} from 'react';
import { CircularProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';

import FirebaseContext from '../../../../context/FirebaseContext';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import ChatContext from '../../../../context/ChatContext';
import { attachmentPreviewPropTypes } from '../../../../utils/attachments';
import {
  StyledAudio,
  TranscriptionWrapper,
  TranscriptionText,
  LoaderContainer,
  ButtonContainer,
  StyledButton,
  ErrorText,
} from './styles';
import texts from './texts.json';

const defaultAudioType = 'audio/mp3';

const AudioError = {
  MEDIA_ERR_ABORTED: 'MEDIA_ERR_ABORTED',
  MEDIA_ERR_NETWORK: 'MEDIA_ERR_NETWORK',
  MEDIA_ERR_DECODE: 'MEDIA_ERR_DECODE',
  MEDIA_ERR_SRC_NOT_SUPPORTED: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
};

const AudioErrorCode = {
  [AudioError.MEDIA_ERR_ABORTED]: 1,
  [AudioError.MEDIA_ERR_NETWORK]: 2,
  [AudioError.MEDIA_ERR_DECODE]: 3,
  [AudioError.MEDIA_ERR_SRC_NOT_SUPPORTED]: 4,
};

const audioErrorMessages = {
  [AudioErrorCode[AudioError.MEDIA_ERR_ABORTED]]: 'User aborted the audio playback',
  [AudioErrorCode[AudioError.MEDIA_ERR_NETWORK]]: 'Network error',
  [AudioErrorCode[AudioError.MEDIA_ERR_DECODE]]: 'Audio decoding error',
  [AudioErrorCode[AudioError.MEDIA_ERR_SRC_NOT_SUPPORTED]]: 'Audio not supported',
};

const AudioMessageAttachment = ({
  attachmentData,
  assetLocalUrl,
}) => {
  const attachmentTranscript = (attachmentData?.transcript || '').trim();
  const [transcription, setTranscription] = useState(attachmentTranscript);
  const [isLoadingTranscription, setIsLoadingTranscription] = useState(false);
  const [showTranscription, setShowTranscription] = useState(!!attachmentTranscript);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  const isComponentMountedRef = useComponentMounted();
  const {
    firebase: {
      remote,
    },
  } = useContext(FirebaseContext);
  const { chatUser } = useContext(ChatContext);

  useEffect(() => {
    // audio error handling
    const audioReference = audioRef.current;
    if (audioReference) {
      audioReference.onerror = (err) => {
        if (err.target.error) {
          const { code } = err.target.error;
          setError(audioErrorMessages[code] || texts.audioError);
        } else {
          setError(texts.audioError);
        }
      };
    }

    return () => {
      if (audioReference) {
        audioReference.onerror = null;
      }
    };
  }, []);

  useEffect(() => {
    if (attachmentTranscript && !transcription) {
      setTranscription(attachmentTranscript);
      setShowTranscription(true);
    }
  }, [attachmentTranscript, transcription]);

  const loadTranscription = useCallback(async () => {
    setShowTranscription((prev) => !prev);

    if (!transcription) {
      setIsLoadingTranscription(true);
      try {
        const response = await remote('chatMessaging-getAudioTranscription', {
          audioPath: attachmentData.assetPath,
          coachId: chatUser.id,
        });
        const data = await response.json();
        if (isComponentMountedRef) {
          if (response.ok) {
            setTranscription(data.transcript || texts.noTranscription);
          } else {
            setTranscription(texts.transcriptionError);
          }
        }
      } catch (err) {
        if (isComponentMountedRef) {
          setTranscription(texts.transcriptionError);
        }
      } finally {
        if (isComponentMountedRef.current) {
          setIsLoadingTranscription(false);
        }
      }
    }
  }, [
    isComponentMountedRef,
    remote,
    attachmentData,
    transcription,
    chatUser,
  ]);

  return (
    <>
      <StyledAudio controls ref={audioRef}>
        <source src={assetLocalUrl} type={attachmentData.mimeType} />
        <source src={assetLocalUrl} type={defaultAudioType} />
      </StyledAudio>
      {error && <ErrorText>{error}</ErrorText>}
      <TranscriptionWrapper>
        <TranscriptionText>
          {showTranscription && transcription}
          {isLoadingTranscription && (
          <LoaderContainer>
            <CircularProgress size={20} />
          </LoaderContainer>
          )}
          <ButtonContainer>
            <StyledButton
              onClick={loadTranscription}
              disabled={isLoadingTranscription}
            >
              <MicIcon />
              { showTranscription ? texts.collapseView : texts.voiceTranscript }
            </StyledButton>
          </ButtonContainer>
        </TranscriptionText>
      </TranscriptionWrapper>
    </>
  );
};

AudioMessageAttachment.propTypes = attachmentPreviewPropTypes;

AudioMessageAttachment.defaultProps = {};

export default AudioMessageAttachment;
